<script>
import './FinanceTypes.styl'
import {financeTypesApi} from '@/api/admin/financeTypes.js'
import PlaneCheckbox from '@/components/common/Checkbox/PlaneCheckbox.vue'
import InfoPopup from '@/components/common/modals/InfoPopup'
import QuestionPopup from '@/components/common/modals/QuestionPopup'
import Spinner from '@/components/common/Spinner/Spinner.vue'

const TABS = {
  fees: 'fees',
  taxes: 'taxes',
  markups: 'markups',
  costRangeMarkups: 'costRangeMarkups',
}

const TAXABLE_ITEMS_OPTIONS = [
  {value: 1, label: 'Inclusive cost only'},
  {value: 2, label: 'Inclusive cost and all fees'},
  {value: 3, label: 'Inclusive cost and fees except donations'},
  {value: 4, label: 'Fees except donations'},
]

export default {
  name: 'finance-types',
  data: () => ({
    activeTab: TABS.fees,
    fees: [],
    taxes: [],
    markups: [],
    costRangeMarkups: [],
    savingId: null,
    deletingId: null,
    pendingDeleteId: null,
    loading: false,
    taxableItemsOptions: TAXABLE_ITEMS_OPTIONS,
    ops: {
      rail: {
        size: '3px',
      },
      bar: {
        size: '3px',
        background: 'rgba(0,0,0,0.1)',
      },
    },
  }),
  computed: {
    tabs () {
      return [
        {id: TABS.fees, label: 'Fees types'},
        {id: TABS.taxes, label: 'Taxes types'},
        {id: TABS.markups, label: 'Markup types'},
        {id: TABS.costRangeMarkups, label: 'Cost range markups'},
      ]
    },
    sectionTitle () {
      const tab = this.tabs.find(item => item.id === this.activeTab)
      return tab ? tab.label : ''
    },
  },
  async created () {
    await this.loadAll()
  },
  methods: {
    async loadAll () {
      await this.executeIntegration(async () => {
        const [feesResponse, taxesResponse, markupsResponse, costRangeMarkupsResponse] = await Promise.all([
          financeTypesApi.getFees(),
          financeTypesApi.getTaxes(),
          financeTypesApi.getMarkups(),
          financeTypesApi.getCostRangeMarkups(),
        ])

        this.fees = this.normalizeList(feesResponse.data).map(item => ({
          id: item.id,
          name: item.name || '',
          percentage: item.percentage,
          textValue: item.textValue || '',
          isForDonation: !!item.isForDonation,
          order: item.order,
        }))

        this.taxes = this.normalizeList(taxesResponse.data).map(item => ({
          id: item.id,
          name: item.name || '',
          percentage: item.percentage,
          taxableItems: item.taxableItems,
          order: item.order,
        }))

        this.markups = this.normalizeList(markupsResponse.data).map(item => ({
          id: item.id,
          name: item.name || '',
          percentage: item.percentage,
        }))

        this.costRangeMarkups = this.normalizeList(costRangeMarkupsResponse.data).map(this.mapCostRangeMarkup)
      })
    },
    mapCostRangeMarkup (item) {
      return {
        id: item.id,
        from: item.from,
        to: item.to,
        percentage: item.percentage,
        isNew: false,
      }
    },
    normalizeList (data) {
      if (Array.isArray(data)) {
        return data
      }

      if (Array.isArray(data?.item)) {
        return data.item
      }

      return []
    },
    setTab (tab) {
      this.activeTab = tab
    },
    validateCommon (item) {
      if (!item.name || !String(item.name).trim()) {
        this.showInfoDialog('Name is required')
        return false
      }

      const percentage = Number(item.percentage)
      if (Number.isNaN(percentage) || percentage < 0) {
        this.showInfoDialog('Percentage must be a non-negative number')
        return false
      }

      return true
    },
    validateOrder (order) {
      const value = Number(order)
      if (!Number.isInteger(value) || value < 0 || value > 255) {
        this.showInfoDialog('Order must be an integer from 0 to 255')
        return false
      }

      return true
    },
    validateCostRangeMarkup (item) {
      const from = Number(item.from)
      const to = Number(item.to)
      const percentage = Number(item.percentage)

      if (Number.isNaN(from) || Number.isNaN(to) || Number.isNaN(percentage)) {
        this.showInfoDialog('From, To and Percentage must be numbers')
        return false
      }

      if (from > to) {
        this.showInfoDialog("'From' must be less than or equal to 'To'")
        return false
      }

      if (percentage < 0) {
        this.showInfoDialog('Percentage must be greater than or equal to 0')
        return false
      }

      return true
    },
    async saveFee (item) {
      if (!this.validateCommon(item) || !this.validateOrder(item.order)) {
        return
      }

      const payload = {
        name: String(item.name).trim(),
        percentage: Number(item.percentage),
        textValue: item.textValue || '',
        isForDonation: !!item.isForDonation,
        order: Number(item.order),
      }

      await this.saveItem(item.id, () => financeTypesApi.updateFee(item.id, payload))
    },
    async saveTax (item) {
      if (!this.validateCommon(item) || !this.validateOrder(item.order)) {
        return
      }

      if (!item.taxableItems) {
        this.showInfoDialog('Taxable items is required')
        return
      }

      const payload = {
        name: String(item.name).trim(),
        percentage: Number(item.percentage),
        taxableItems: Number(item.taxableItems),
        order: Number(item.order),
      }

      await this.saveItem(item.id, () => financeTypesApi.updateTax(item.id, payload))
    },
    async saveMarkup (item) {
      if (!this.validateCommon(item)) {
        return
      }

      const payload = {
        name: String(item.name).trim(),
        percentage: Number(item.percentage),
      }

      await this.saveItem(item.id, () => financeTypesApi.updateMarkup(item.id, payload))
    },
    addCostRangeMarkup () {
      if (this.costRangeMarkups.some(item => item.isNew)) {
        this.showInfoDialog('Finish creating the current cost range markup first')
        return
      }

      this.costRangeMarkups.push({
        id: `new-${Date.now()}`,
        from: 0,
        to: 0,
        percentage: 0,
        isNew: true,
      })
    },
    async saveCostRangeMarkup (item) {
      if (!this.validateCostRangeMarkup(item)) {
        return
      }

      const payload = {
        from: Number(item.from),
        to: Number(item.to),
        percentage: Number(item.percentage),
      }

      if (item.isNew) {
        await this.saveItem(item.id, async () => {
          const response = await financeTypesApi.createCostRangeMarkup(payload)

          if (response.data?.isSuccessfull === false) {
            return response
          }

          const created = response.data?.item || response.data
          const index = this.costRangeMarkups.findIndex(row => row.id === item.id)

          if (index !== -1 && created?.id) {
            this.$set(this.costRangeMarkups, index, this.mapCostRangeMarkup(created))
          } else {
            await this.reloadCostRangeMarkups()
          }

          return response
        })
        return
      }

      await this.saveItem(item.id, () => financeTypesApi.updateCostRangeMarkup(item.id, payload))
    },
    async reloadCostRangeMarkups () {
      const response = await financeTypesApi.getCostRangeMarkups()
      this.costRangeMarkups = this.normalizeList(response.data).map(this.mapCostRangeMarkup)
    },
    confirmDeleteCostRangeMarkup (item) {
      if (item.isNew) {
        this.costRangeMarkups = this.costRangeMarkups.filter(row => row.id !== item.id)
        return
      }

      this.pendingDeleteId = item.id
      this.$modal.show('QuestionPopupDeleteCostRangeMarkup')
    },
    async deleteCostRangeMarkup () {
      const id = this.pendingDeleteId
      this.pendingDeleteId = null

      if (!id) {
        return
      }

      this.deletingId = id

      try {
        await this.executeIntegration(async () => {
          const response = await financeTypesApi.deleteCostRangeMarkup(id)

          if (response.data?.isSuccessfull === false) {
            this.showInfoDialog(response.data?.message || 'Something went wrong')
            return
          }

          this.costRangeMarkups = this.costRangeMarkups.filter(row => row.id !== id)
          this.showInfoDialog('Deleted successfully')
        })
      } catch (e) {
        console.error(e)
        this.showInfoDialog(e?.response?.data?.message || 'Something went wrong')
      } finally {
        this.deletingId = null
      }
    },
    async saveItem (id, request) {
      this.savingId = id

      try {
        await this.executeIntegration(async () => {
          const response = await request()

          if (response.data?.isSuccessfull === false) {
            this.showInfoDialog(response.data?.message || 'Something went wrong')
            return
          }

          this.showInfoDialog('Saved successfully')
        })
      } catch (e) {
        console.error(e)
        this.showInfoDialog(e?.response?.data?.message || 'Something went wrong')
      } finally {
        this.savingId = null
      }
    },
    showInfoDialog (title) {
      this.$modal.show('InfoPopup', {title})
    },
    async executeIntegration (callback) {
      this.loading = true

      try {
        await callback()
      } catch (e) {
        console.error(e)
        this.showInfoDialog(e?.response?.data?.message || 'Something went wrong')
      } finally {
        this.loading = false
      }
    },
  },
  components: {
    Spinner,
    PlaneCheckbox,
    InfoPopup,
    QuestionPopup,
  },
}
</script>

<template>
  <div class="financeTypesDashboard">
    <vue-scroll :ops="ops">
      <div class="financeTypesDashboard__header">
        <div class="financeTypesDashboard__title">Finance params</div>
      </div>

      <div class="financeTypesDashboard__tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="financeTypesDashboard__tab"
          :class="{ active: activeTab === tab.id }"
          @click.prevent="setTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="financeTypesDashboard__section-header">
        <div class="financeTypesDashboard__section-title">{{ sectionTitle }}</div>
        <button
          v-if="activeTab === 'costRangeMarkups'"
          class="addButton"
          @click.prevent="addCostRangeMarkup"
        >
          Add
        </button>
      </div>

      <template v-if="activeTab === 'fees'">
        <div v-if="!fees.length" class="empty">No fee types found</div>
        <div v-else class="table">
          <div class="table-header">
            <div class="table-cell table-cell--name">Name</div>
            <div class="table-cell table-cell--percent">Percentage</div>
            <div class="table-cell">Text value</div>
            <div class="table-cell table-cell--checkbox">Donation</div>
            <div class="table-cell table-cell--order">Order</div>
            <div class="table-cell table-cell--actions"></div>
          </div>
          <div
            v-for="item in fees"
            :key="item.id"
            class="table-row"
          >
            <div class="table-cell table-cell--name">
              <span class="mobile-label">Name:</span>
              <input class="field" v-model="item.name" type="text"/>
            </div>
            <div class="table-cell table-cell--percent">
              <span class="mobile-label">Percentage:</span>
              <input class="field" v-model="item.percentage" type="number" min="0" step="0.01"/>
            </div>
            <div class="table-cell">
              <span class="mobile-label">Text value:</span>
              <input class="field" v-model="item.textValue" type="text"/>
            </div>
            <div class="table-cell table-cell--checkbox">
              <span class="mobile-label">Donation:</span>
              <PlaneCheckbox :isChecked.sync="item.isForDonation"/>
            </div>
            <div class="table-cell table-cell--order">
              <span class="mobile-label">Order:</span>
              <input class="field" v-model="item.order" type="number" min="0" max="255" step="1"/>
            </div>
            <div class="table-cell table-cell--actions">
              <button
                class="saveButton"
                :disabled="savingId === item.id"
                @click.prevent="saveFee(item)"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="activeTab === 'taxes'">
        <div v-if="!taxes.length" class="empty">No tax types found</div>
        <div v-else class="table">
          <div class="table-header">
            <div class="table-cell table-cell--name">Name</div>
            <div class="table-cell table-cell--percent">Percentage</div>
            <div class="table-cell table-cell--taxable">Taxable items</div>
            <div class="table-cell table-cell--order">Order</div>
            <div class="table-cell table-cell--actions"></div>
          </div>
          <div
            v-for="item in taxes"
            :key="item.id"
            class="table-row"
          >
            <div class="table-cell table-cell--name">
              <span class="mobile-label">Name:</span>
              <input class="field" v-model="item.name" type="text"/>
            </div>
            <div class="table-cell table-cell--percent">
              <span class="mobile-label">Percentage:</span>
              <input class="field" v-model="item.percentage" type="number" min="0" step="0.01"/>
            </div>
            <div class="table-cell table-cell--taxable">
              <span class="mobile-label">Taxable items:</span>
              <select class="selectField" v-model.number="item.taxableItems">
                <option
                  v-for="option in taxableItemsOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="table-cell table-cell--order">
              <span class="mobile-label">Order:</span>
              <input class="field" v-model="item.order" type="number" min="0" max="255" step="1"/>
            </div>
            <div class="table-cell table-cell--actions">
              <button
                class="saveButton"
                :disabled="savingId === item.id"
                @click.prevent="saveTax(item)"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="activeTab === 'markups'">
        <div v-if="!markups.length" class="empty">No markup types found</div>
        <div v-else class="table">
          <div class="table-header">
            <div class="table-cell table-cell--name">Name</div>
            <div class="table-cell table-cell--percent">Percentage</div>
            <div class="table-cell table-cell--actions"></div>
          </div>
          <div
            v-for="item in markups"
            :key="item.id"
            class="table-row"
          >
            <div class="table-cell table-cell--name">
              <span class="mobile-label">Name:</span>
              <input class="field" v-model="item.name" type="text"/>
            </div>
            <div class="table-cell table-cell--percent">
              <span class="mobile-label">Percentage:</span>
              <input class="field" v-model="item.percentage" type="number" min="0" step="0.01"/>
            </div>
            <div class="table-cell table-cell--actions">
              <button
                class="saveButton"
                :disabled="savingId === item.id"
                @click.prevent="saveMarkup(item)"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div v-if="!costRangeMarkups.length" class="empty">No cost range markups found</div>
        <div v-else class="table">
          <div class="table-header">
            <div class="table-cell table-cell--range">From</div>
            <div class="table-cell table-cell--range">To</div>
            <div class="table-cell table-cell--percent">Percentage</div>
            <div class="table-cell table-cell--actions table-cell--actions-wide"></div>
          </div>
          <div
            v-for="item in costRangeMarkups"
            :key="item.id"
            class="table-row"
          >
            <div class="table-cell table-cell--range">
              <span class="mobile-label">From:</span>
              <input class="field" v-model="item.from" type="number" min="0" step="0.01"/>
            </div>
            <div class="table-cell table-cell--range">
              <span class="mobile-label">To:</span>
              <input class="field" v-model="item.to" type="number" min="0" step="0.01"/>
            </div>
            <div class="table-cell table-cell--percent">
              <span class="mobile-label">Percentage:</span>
              <input class="field" v-model="item.percentage" type="number" min="0" step="0.01"/>
            </div>
            <div class="table-cell table-cell--actions table-cell--actions-wide">
              <button
                class="saveButton"
                :disabled="savingId === item.id || deletingId === item.id"
                @click.prevent="saveCostRangeMarkup(item)"
              >
                {{ item.isNew ? 'Create' : 'Save' }}
              </button>
              <button
                class="deleteButton"
                :disabled="savingId === item.id || deletingId === item.id"
                @click.prevent="confirmDeleteCostRangeMarkup(item)"
              >
                {{ item.isNew ? 'Cancel' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <InfoPopup/>
      <QuestionPopup
        :name="'QuestionPopupDeleteCostRangeMarkup'"
        :title="'Cost range markup will be removed'"
        :text="'Are you sure?'"
        @remove="deleteCostRangeMarkup"
      />
    </vue-scroll>

    <div v-if="loading" class="spinner-overlay">
      <Spinner/>
    </div>
  </div>
</template>
